import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import LinkIcon from "@mui/icons-material/Link";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FacebookIcon from "@mui/icons-material/Facebook";
import XIcon from "@mui/icons-material/X";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import TelegramIcon from "@mui/icons-material/Telegram";
import InstagramIcon from "@mui/icons-material/Instagram";
import RedditIcon from "@mui/icons-material/Reddit";
import {
  alpha,
  Box,
  Divider,
  IconButton,
  Stack,
  TextField,
  Fade,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";

// Styled components with glassmorphic design
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(12px)",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
  },
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(3),
    background: `linear-gradient(145deg, 
      ${alpha(theme.palette.background.paper, 0.95)} 0%, 
      ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
    backdropFilter: "blur(20px)",
    border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
    boxShadow: `
      0 8px 32px ${alpha(theme.palette.common.black, 0.15)},
      0 2px 16px ${alpha(theme.palette.common.black, 0.1)},
      inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}
    `,
    minWidth: 500,
    maxWidth: 600,
  },
}));

const StyledIconButton = styled(IconButton)(({ theme, socialcolor }) => ({
  width: 56,
  height: 56,
  background: `linear-gradient(145deg, 
    ${alpha(socialcolor || theme.palette.primary.main, 0.15)} 0%, 
    ${alpha(socialcolor || theme.palette.primary.main, 0.05)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(socialcolor || theme.palette.primary.main, 0.2)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: `linear-gradient(145deg, 
      ${alpha(socialcolor || theme.palette.primary.main, 0.25)} 0%, 
      ${alpha(socialcolor || theme.palette.primary.main, 0.15)} 100%)`,
    transform: "translateY(-2px) scale(1.05)",
    boxShadow: `0 8px 25px ${alpha(
      socialcolor || theme.palette.primary.main,
      0.3
    )}`,
  },
  "&:active": {
    transform: "translateY(0) scale(0.98)",
  },
}));

const ScrollContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: theme.spacing(2),
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.default, 0.3)} 0%, 
    ${alpha(theme.palette.background.default, 0.1)} 100%)`,
  backdropFilter: "blur(5px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  padding: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(2),
    background: `linear-gradient(145deg, 
      ${alpha(theme.palette.background.paper, 0.8)} 0%, 
      ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    transition: "all 0.3s ease",
    "&:hover": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
    "&.Mui-focused": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
}));

export default function ShareView({
  isOpen,
  setIsOpen,
  shareurl,
  shareTitle = "",
  shareImage = "",
}) {
  const linkRef = useRef(null);
  const listRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Social media platforms with MUI icons and brand colors
  const medias = [
    {
      media: "X",
      icon: XIcon,
      color: "#ffffff",
      component: ({ children, url }) => (
        <a
          href={`https://x.com/intent/post?url=${encodeURIComponent(
            url
          )}&text=${encodeURIComponent(shareTitle)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          {children}
        </a>
      ),
    },
    {
      media: "LinkedIn",
      icon: LinkedInIcon,
      color: "#0A66C2",
      component: ({ children, url }) => (
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            url
          )}&title=${encodeURIComponent(
            shareTitle
          )}&summary=${encodeURIComponent(shareTitle)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          {children}
        </a>
      ),
    },
    {
      media: "Facebook",
      icon: FacebookIcon,
      color: "#1877F2",
      component: ({ children, url }) => (
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            url
          )}&quote=${encodeURIComponent(shareTitle)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          {children}
        </a>
      ),
    },
    {
      media: "Reddit",
      icon: RedditIcon,
      color: "#FF4500",
      component: ({ children, url }) => (
        <a
          href={`https://reddit.com/submit?url=${encodeURIComponent(
            url
          )}&title=${encodeURIComponent(shareTitle)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          {children}
        </a>
      ),
    },
    {
      media: "WhatsApp",
      icon: WhatsAppIcon,
      color: "#25D366",
      component: ({ children, url }) => (
        <a
          href={`https://wa.me/?text=${encodeURIComponent(
            `${shareTitle} ${url}`
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          {children}
        </a>
      ),
    },
    {
      media: "Telegram",
      icon: TelegramIcon,
      color: "#0088CC",
      component: ({ children, url }) => (
        <a
          href={`https://t.me/share/url?url=${encodeURIComponent(
            url
          )}&text=${encodeURIComponent(shareTitle)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          {children}
        </a>
      ),
    },

    {
      media: "Instagram",
      icon: InstagramIcon,
      color: "#E1306C",
      component: ({ children, url }) => (
        <a
          href={`https://www.instagram.com/`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          {children}
        </a>
      ),
    },
  ];

  const handleCopyLink = useCallback(async () => {
    try {
      await window.navigator.clipboard.writeText(shareurl);
      if (linkRef.current) {
        linkRef.current.select();
      }
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      // Fallback for older browsers
      if (linkRef.current) {
        linkRef.current.select();
        document.execCommand("copy");
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      }
    }
  }, [shareurl]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      setIsCopied(false);
    }, 150);
  }, [setIsOpen]);

  const updateScrollButtons = useCallback(() => {
    if (listRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = listRef.current;
      const maxScrollLeft = scrollWidth - clientWidth;

      // More precise end detection
      setCanScrollLeft(scrollLeft > 1);
      setCanScrollRight(scrollLeft < maxScrollLeft - 1);
    }
  }, []);

  const scrollTo = useCallback((direction) => {
    if (listRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;

      listRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
      updateScrollButtons();
    }
  }, []);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    updateScrollButtons();
  }, [updateScrollButtons]);

  // Setup scroll listener with immediate and delayed updates
  useEffect(() => {
    const currentList = listRef.current;
    if (currentList) {
      currentList.addEventListener("scroll", handleScroll, { passive: true });

      // Additional check after scroll stops
      let scrollTimer;
      const handleScrollEnd = () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(updateScrollButtons, 50);
      };

      currentList.addEventListener("scroll", handleScrollEnd, {
        passive: true,
      });

      return () => {
        currentList.removeEventListener("scroll", handleScroll);
        currentList.removeEventListener("scroll", handleScrollEnd);
        clearTimeout(scrollTimer);
      };
    }
  }, [handleScroll, updateScrollButtons]);

  // Initialize scroll state when modal opens
  useLayoutEffect(() => {
    if (isOpen) {
      setTimeout(updateScrollButtons, 100);
    }
  }, [isOpen, updateScrollButtons]);

  // Reset state when modal closes
  useLayoutEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setIsCopied(false);
        setCanScrollLeft(false);
        setCanScrollRight(false);
      }, 200);
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === "Escape") {
        handleClose();
      } else if (e.key === "ArrowLeft" && canScrollLeft) {
        scrollTo("left");
      } else if (e.key === "ArrowRight" && canScrollRight) {
        scrollTo("right");
      } else if ((e.ctrlKey || e.metaKey) && e.key === "c") {
        e.preventDefault();
        handleCopyLink();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    isOpen,
    canScrollLeft,
    canScrollRight,
    handleClose,
    scrollTo,
    handleCopyLink,
  ]);

  if (!shareurl) return null;

  return (
    <StyledDialog
      open={isOpen && !isClosing}
      onClose={handleClose}
      TransitionProps={{
        timeout: 300,
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6">Share Article</Typography>
          </Box>
          <Tooltip title="Close (Esc)">
            <IconButton
              onClick={handleClose}
              size="small"
              color="error"
              sx={{
                bgcolor: alpha("#fff", 0.1),
                backdropFilter: "blur(10px)",
                "&:hover": {
                  bgcolor: alpha("#fff", 0.2),
                },
                transition: "all 0.3s ease",
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <Divider sx={{ borderColor: alpha("#fff", 0.1) }} />

      {/* Content Preview Section */}
      {(shareTitle || shareImage) && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, opacity: 0.8 }}>
            Preview
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              background: `linear-gradient(145deg, 
                ${alpha("#fff", 0.05)} 0%, 
                ${alpha("#fff", 0.02)} 100%)`,
              border: `1px solid ${alpha("#fff", 0.1)}`,
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            {shareImage && (
              <Box
                sx={{
                  width: 80,
                  height: 60,
                  borderRadius: 1,
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <img
                  src={shareImage}
                  alt="Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            )}
            {shareTitle && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    mb: 0.5,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {shareTitle}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {shareurl}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Social Media Section */}
      <Box sx={{ p: 3, pb: 2 }}>
        <ScrollContainer>
          <Fade in={canScrollLeft}>
            <IconButton
              onClick={() => scrollTo("left")}
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                bgcolor: alpha("#000", 0.6),
                backdropFilter: "blur(10px)",
                color: "white",
                "&:hover": {
                  bgcolor: alpha("#000", 0.8),
                  transform: "translateY(-50%) scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
              size="small"
            >
              <NavigateBeforeRoundedIcon fontSize="small" />
            </IconButton>
          </Fade>

          <List
            ref={listRef}
            sx={{
              display: "flex",
              overflowX: "auto",
              overflowY: "hidden",
              py: 2,
              px: 1,
              gap: 2,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              minHeight: 100,
            }}
          >
            {medias?.map((media, index) => {
              const ShareComponent = media.component;
              const ShareIcon = media.icon;
              return (
                <Fade in timeout={200 + index * 100} key={index}>
                  <ListItem
                    disableGutters
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      minWidth: 80,
                      maxWidth: 80,
                      gap: 1,
                    }}
                  >
                    <ShareComponent url={shareurl}>
                      <Tooltip title={`Share on ${media.media}`}>
                        <StyledIconButton socialcolor={media.color}>
                          <ShareIcon
                            sx={{ fontSize: 24, color: media.color }}
                          />
                        </StyledIconButton>
                      </Tooltip>
                    </ShareComponent>
                    <ListItemText
                      primary={media.media}
                      sx={{
                        textAlign: "center",
                        "& .MuiListItemText-primary": {
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          opacity: 0.8,
                        },
                      }}
                    />
                  </ListItem>
                </Fade>
              );
            })}
          </List>

          <Fade in={canScrollRight}>
            <IconButton
              onClick={() => scrollTo("right")}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                bgcolor: alpha("#000", 0.6),
                backdropFilter: "blur(10px)",
                color: "white",
                "&:hover": {
                  bgcolor: alpha("#000", 0.8),
                  transform: "translateY(-50%) scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
              size="small"
            >
              <NavigateNextRoundedIcon fontSize="small" />
            </IconButton>
          </Fade>
        </ScrollContainer>
      </Box>

      {/* Copy Link Section */}
      <Box sx={{ px: 3, pb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, opacity: 0.8 }}>
          Or copy link
        </Typography>

        <StyledTextField
          fullWidth
          value={shareurl}
          inputRef={linkRef}
          placeholder="Article URL"
          InputProps={{
            readOnly: true,
            startAdornment: (
              <LinkIcon sx={{ mr: 1, opacity: 0.6, fontSize: 20 }} />
            ),
            endAdornment: (
              <Tooltip title={isCopied ? "Copied!" : "Copy link (Ctrl+C)"}>
                <IconButton
                  onClick={handleCopyLink}
                  sx={{
                    bgcolor: isCopied
                      ? alpha("#4CAF50", 0.2)
                      : alpha("#667eea", 0.15),
                    backdropFilter: "blur(10px)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: isCopied
                        ? alpha("#4CAF50", 0.3)
                        : alpha("#667eea", 0.25),
                      transform: "scale(1.05)",
                    },
                  }}
                  size="small"
                >
                  <Fade in={isCopied} timeout={200}>
                    <DoneRoundedIcon
                      fontSize="small"
                      sx={{
                        color: "#4CAF50",
                        position: isCopied ? "static" : "absolute",
                        opacity: isCopied ? 1 : 0,
                      }}
                    />
                  </Fade>
                  <Fade in={!isCopied} timeout={200}>
                    <ContentCopyRoundedIcon
                      fontSize="small"
                      sx={{
                        position: isCopied ? "absolute" : "static",
                        opacity: isCopied ? 0 : 1,
                      }}
                    />
                  </Fade>
                </IconButton>
              </Tooltip>
            ),
          }}
          sx={{
            "& .MuiInputBase-input": {
              fontSize: "0.875rem",
              py: 1.5,
            },
          }}
        />
      </Box>
    </StyledDialog>
  );
}

ShareView.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  shareurl: PropTypes.string.isRequired,
  shareTitle: PropTypes.string,
  shareImage: PropTypes.string,
};
